/** Keyboard entry point for the existing native photo viewer. */
export class KalesProductZoomTrigger extends HTMLElement {
  #controller;

  async connectedCallback() {
    this.#controller?.abort();
    const controller = new AbortController();
    this.#controller = controller;
    const button = this.querySelector('button');
    const gallery = this.closest('.product-information__media')?.querySelector('media-gallery');
    const zoom = gallery?.querySelector('zoom-dialog');
    if (!button || !zoom) return;
    await customElements.whenDefined('zoom-dialog');
    if (controller.signal.aborted || !this.isConnected) return;
    button.hidden = false;
    button.addEventListener('click', (event) => {
      // Product selection can replace only the gallery, retaining this sibling.
      const currentGallery = this.closest('.product-information__media')?.querySelector('media-gallery');
      const currentZoom = currentGallery?.querySelector('zoom-dialog');
      if (typeof currentZoom?.open !== 'function') { button.hidden = true; return; }
      const slideshow = currentGallery.querySelector('slideshow-component');
      currentZoom.open(slideshow?.current ?? 0, event);
    }, { signal: controller.signal });
  }

  disconnectedCallback() {
    this.#controller?.abort();
  }
}

if (!customElements.get('kales-product-zoom-trigger')) {
  customElements.define('kales-product-zoom-trigger', KalesProductZoomTrigger);
}

/** One editable guide per product; the dialog is only a view of that content. */
export class KalesSizeGuideTrigger extends HTMLElement {
  #controller;
  #dialog;

  connectedCallback() {
    this.#controller?.abort();
    this.#controller = new AbortController();
    const { signal } = this.#controller;
    const button = this.querySelector('button');
    if (!button) return;
    const refresh = () => { button.hidden = !this.#source(); };
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', refresh, { once: true, signal });
    } else refresh();
    document.addEventListener('shopify:section:load', refresh, { signal });
    document.addEventListener('shopify:section:unload', (event) => {
      const source = this.#source();
      if (source && event.target instanceof Element && event.target.contains(source)) {
        button.hidden = true;
        this.#dialog?.close();
      }
    }, { signal });
    button.addEventListener('click', () => this.#open(button), { signal });
  }

  #source() {
    const productId = this.dataset.productId;
    if (!productId || typeof HTMLDialogElement === 'undefined') return null;
    return [...document.querySelectorAll('.kales-size-guide[data-product-id]')]
      .find((guide) => guide.dataset.productId === productId && guide.querySelector('.kales-size-guide__body'));
  }

  #open(button) {
    const source = this.#source();
    if (!source) { button.hidden = true; return; }
    if (this.#dialog?.open) return;
    const dialog = document.createElement('dialog');
    dialog.className = 'kales-size-modal';
    const heading = document.createElement('h2');
    heading.id = `kales-size-modal-title-${this.dataset.productId}`;
    heading.textContent = source.querySelector('summary')?.firstChild?.textContent?.trim() || 'Guía de tallas';
    dialog.setAttribute('aria-labelledby', heading.id);
    const header = document.createElement('div');
    header.className = 'kales-size-modal__header';
    const close = document.createElement('button');
    close.type = 'button';
    close.className = 'kales-size-modal__close';
    close.setAttribute('aria-label', 'Cerrar guía de tallas');
    const closeIcon = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
    closeIcon.setAttribute('viewBox', '0 0 20 20');
    closeIcon.setAttribute('aria-hidden', 'true');
    closeIcon.setAttribute('focusable', 'false');
    const closePath = document.createElementNS('http://www.w3.org/2000/svg', 'path');
    closePath.setAttribute('d', 'M4 4l12 12M16 4L4 16');
    closePath.setAttribute('fill', 'none');
    closePath.setAttribute('stroke', 'currentColor');
    closePath.setAttribute('stroke-width', '1.5');
    closePath.setAttribute('stroke-linecap', 'round');
    closeIcon.append(closePath);
    close.append(closeIcon);
    header.append(heading, close);
    const body = source.querySelector('.kales-size-guide__body').cloneNode(true);
    // Content may contain merchant-entered IDs; avoid duplicates with its original.
    body.querySelectorAll('[id]').forEach((element) => element.removeAttribute('id'));
    dialog.append(header, body);
    close.addEventListener('click', () => dialog.close());
    dialog.addEventListener('click', (event) => {
      if (event.target !== dialog) return;
      const rect = dialog.getBoundingClientRect();
      if (event.clientX < rect.left || event.clientX > rect.right || event.clientY < rect.top || event.clientY > rect.bottom) dialog.close();
    });
    dialog.addEventListener('close', () => {
      document.documentElement.classList.remove('kales-size-modal-open');
      dialog.remove();
      if (this.#dialog === dialog) this.#dialog = null;
      if (button.isConnected) button.focus({ preventScroll: true });
    }, { once: true });
    document.body.append(dialog);
    this.#dialog = dialog;
    dialog.showModal();
    document.documentElement.classList.add('kales-size-modal-open');
    close.focus();
  }

  disconnectedCallback() {
    this.#controller?.abort();
    this.#dialog?.close();
  }
}

if (!customElements.get('kales-size-guide-trigger')) {
  customElements.define('kales-size-guide-trigger', KalesSizeGuideTrigger);
}
